// src/services/apiClient.js
import { REQUEST_TIMEOUT_MS, STORAGE_KEYS } from "../core/config";

function authHeaderObject() {
  try {
    const token = window.localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    if (!token) return {};
    const value = token.startsWith("Bearer ") ? token : `Bearer ${token}`;
    return { Authorization: value };
  } catch {
    return {};
  }
}

/** Combine two AbortSignals so either can cancel the combined operation. */
export function mergeAbortSignals(a, b) {
  if (!a) return b;
  if (!b) return a;
  const c = new AbortController();
  const abort = () => {
    if (!c.signal.aborted) c.abort();
  };
  if (a.aborted || b.aborted) {
    abort();
    return c.signal;
  }
  a.addEventListener("abort", abort, { once: true });
  b.addEventListener("abort", abort, { once: true });
  return c.signal;
}

/**
 * Minimal JSON POST helper.
 */
export async function postJson(url, payload, options = {}) {
  const { signal, timeout = REQUEST_TIMEOUT_MS } = options;

  let timeoutId = null;
  let timeoutController = null;
  if (timeout > 0) {
    timeoutController = new AbortController();
    timeoutId = setTimeout(() => {
      timeoutController.abort();
    }, timeout);
  }

  const mergedSignal = mergeAbortSignals(signal, timeoutController?.signal);

  try {
    const requestBody = JSON.stringify(payload);

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...authHeaderObject(),
      },
      body: requestBody,
      signal: mergedSignal,
    });

    if (timeoutId) clearTimeout(timeoutId);

    if (!response.ok) {
      if (response.status === 413) {
        throw new Error(
          "The upload is too large for the server (413). Try a smaller file, or ensure nginx is configured with client_max_body_size (e.g. 100m) and rebuild the UI container."
        );
      }
      let errorText = "";
      let errorDetails = null;
      try {
        const errorData = await response.json();
        errorDetails = errorData;
        errorText = errorData.detail || errorData.message || errorData.error || JSON.stringify(errorData);
      } catch (e) {
        errorText = await response.text().catch(() => response.statusText);
      }

      throw new Error(
        `API error (${response.status}): ${errorText || response.statusText}`
      );
    }

    try {
      return await response.json();
    } catch {
      throw new Error("Failed to parse server response as JSON.");
    }
  } catch (error) {
    if (timeoutId) clearTimeout(timeoutId);

    if (error.name === "AbortError") {
      throw error;
    }
    if (error.message.includes("aborted")) {
      throw new Error("Request timed out. The server is taking too long to respond. Please try again.");
    }

    if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
      throw new Error("Network error: Unable to connect to the server. Please check your connection and try again.");
    }

    throw error;
  }
}

/**
 * JSON GET helper (dashboard and other read endpoints).
 */
export async function getJson(url, options = {}) {
  const { signal, timeout = 120000 } = options;

  let controller = signal;
  let timeoutId = null;

  if (!controller && timeout > 0) {
    controller = new AbortController();
    timeoutId = setTimeout(() => {
      controller.abort();
    }, timeout);
  }

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: { Accept: "application/json", ...authHeaderObject() },
      signal: controller?.signal,
    });

    if (timeoutId) clearTimeout(timeoutId);

    if (!response.ok) {
      let errorText = "";
      try {
        const errorData = await response.json();
        errorText = errorData.detail || errorData.message || errorData.error || JSON.stringify(errorData);
      } catch {
        errorText = await response.text().catch(() => response.statusText);
      }
      throw new Error(`API error (${response.status}): ${errorText || response.statusText}`);
    }

    try {
      return await response.json();
    } catch {
      throw new Error("Failed to parse server response as JSON.");
    }
  } catch (error) {
    if (timeoutId) clearTimeout(timeoutId);

    if (error.name === "AbortError" || error.message.includes("aborted")) {
      throw new Error("Request timed out. Please try again.");
    }

    if (error.message.includes("Failed to fetch") || error.message.includes("NetworkError")) {
      throw new Error("Network error: Unable to connect to the server. Please check your connection and try again.");
    }

    throw error;
  }
}

/**
 * JSON DELETE helper (e.g. dashboard snapshot removal). Accepts empty 204 responses.
 */
export async function deleteJson(url, options = {}) {
  const { signal, timeout = 60000 } = options;

  let controller = signal;
  let timeoutId = null;

  if (!controller && timeout > 0) {
    controller = new AbortController();
    timeoutId = setTimeout(() => {
      controller.abort();
    }, timeout);
  }

  try {
    const response = await fetch(url, {
      method: "DELETE",
      headers: { Accept: "application/json", ...authHeaderObject() },
      signal: controller?.signal,
    });

    if (timeoutId) clearTimeout(timeoutId);

    if (!response.ok) {
      let errorText = "";
      try {
        const errorData = await response.json();
        errorText = errorData.detail || errorData.message || errorData.error || JSON.stringify(errorData);
      } catch {
        errorText = await response.text().catch(() => response.statusText);
      }
      throw new Error(`API error (${response.status}): ${errorText || response.statusText}`);
    }

    const text = await response.text();
    if (!text?.trim()) return {};
    try {
      return JSON.parse(text);
    } catch {
      return {};
    }
  } catch (error) {
    if (timeoutId) clearTimeout(timeoutId);

    if (error.name === "AbortError" || error.message.includes("aborted")) {
      throw new Error("Request timed out. Please try again.");
    }

    if (error.message.includes("Failed to fetch") || error.message.includes("NetworkError")) {
      throw new Error("Network error: Unable to connect to the server. Please check your connection and try again.");
    }

    throw error;
  }
}
